import add from 'date-fns/add';
import { ajax } from 'rxjs/ajax';
import { firstValueFrom } from 'rxjs';
import esLocale from 'date-fns/locale/es';
import camelcaseKeys from 'camelcase-keys';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import intervalToDuration from 'date-fns/intervalToDuration';
import { formatDuration, format, startOfWeek, endOfWeek, addDays } from 'date-fns';

const { baseApiPCv2 } = environment;

/**
 * short y full: se debe usar para mostrar fechas en la interfaz o en los documentos
 * iso y isoShort: se debe usar para guardar en la base de datos
 * */

export type IntervalTime = 'dia' | 'mes' | 'año' | 'semana' | 'day' | 'month' | 'year' | 'week';
export type FormatTime = 'short' | 'full' | 'iso' | 'isoShort' | 'isoShortDDMMYYYY' | 'isoFirtsHourDate' | 'isoLastHourDate';

export type WorldTime =
  | 'abbreviation'
  | 'clientIp'
  | 'datetime'
  | 'dayOfWeek'
  | 'dayOfYear'
  | 'dst'
  | 'dstFrom'
  | 'dstOffset'
  | 'dstUntil'
  | 'rawOffset'
  | 'timezone'
  | 'unixtime'
  | 'utcDatetime'
  | 'utcOffset'
  | 'weekNumber';

@Injectable({
  providedIn: 'root',
})
export class DateService {

  static currentISOString = new Date().toISOString();

  /**
   * Obtiene la hora actual desde el servidor.
   * @param property - Propiedad específica del objeto WorldTime a retornar. Si es null, retorna el objeto completo.
   * @returns Promesa que resuelve con el objeto WorldTime completo o la propiedad especificada.
   */
  static async getTime(property: WorldTime = null) {
    const url = `${baseApiPCv2}/analitica/v1/hora`;
    const options = { url, method: 'GET', crossDomain: true };

    const { response }: any = await firstValueFrom(ajax(options));
    const worldTime: any = camelcaseKeys(response?.data);

    if (!property) return worldTime;
    return worldTime[property];
  }

  /**
   * Obtiene la fecha y hora en formato chileno según el tipo especificado.
   * @param format - Formato de salida deseado (full, short, iso, isoShort, isoShortDDMMYYYY, isoFirstHourDate, isoLastHourDate)
   * @param date - Fecha a formatear. Si es null, obtiene la fecha actual del servidor
   * @returns Fecha formateada según el formato especificado en zona horaria de Chile (America/Santiago)
   */
  static async datetimeCL(format: FormatTime, date: Date = null) {
    if (!date) {
      const datetimes = await DateService.getTime('datetime');
      date = new Date(datetimes);
    }

    const locales = 'es-CL';
    const timeZone = { timeZone: 'America/Santiago' };

    const localDatetime = date.toLocaleString(locales, timeZone);

    const datetime = {
      full: localDatetime,
      short: localDatetime.split(', ')[0],
      iso: this.toISOLocal(date),
      isoShort: this.toISOLocal(date).split('T')[0],
      isoShortDDMMYYYY: this.formatDateToDDMMYYYY(this.toISOLocal(date).split('T')[0]),
      isoFirstHourDate: this.toISOWithFullHourStartOrEndDay(date, 'start'),
      isoLastHourDate: this.toISOWithFullHourStartOrEndDay(date, 'end'),
    };

    return datetime[format];
  }

  /**
   * Convierte una fecha a diferentes formatos según la zona horaria de Chile.
   * @param format - Formato de salida deseado para la fecha.
   * @param date - Fecha a convertir. Por defecto usa la fecha actual.
   * @returns La fecha formateada según el tipo especificado.
   */
  static datetimeCLResolutions(format: FormatTime, date: Date = new Date()) {
    const locales = 'es-CL';
    const timeZone = { timeZone: 'America/Santiago' };

    const localDatetime = date.toLocaleString(locales, timeZone);

    const datetime = {
      full: localDatetime,
      short: localDatetime.split(', ')[0],
      iso: this.toISOLocal(date),
      isoShort: this.toISOLocal(date).split('T')[0],
      isoFirtsHourDate: this.toISOWithFullHourStartOrEndDay(date, 'start'),
      isoLastHourDate: this.toISOWithFullHourStartOrEndDay(date, 'end'),
    };

    return datetime[format];
  }

  /**
   * Calcula y formatea la edad de una persona desde su fecha de nacimiento.
   * @param birth - Fecha de nacimiento
   * @returns Cadena de texto con los años, meses y días transcurridos en español
   */
  static yearsOld(birth) {
    const birthDate: Date = new Date(birth);
    const duration = intervalToDuration({ start: birthDate, end: new Date() });

    return formatDuration(duration, { format: ['years', 'months', 'days'], locale: esLocale });
  }

  /**
   * Agrega un intervalo de tiempo a una fecha y la retorna formateada.
   * @param format - Formato de salida deseado para la fecha
   * @param interval - Tipo de intervalo a agregar (día, mes, año, semana)
   * @param rangetime - Cantidad de unidades del intervalo a agregar
   * @param date - Fecha base (por defecto: fecha actual)
   * @returns Fecha resultante formateada según el formato especificado
   */
  static addTime(format: FormatTime, interval: IntervalTime, rangetime: number, date: Date = new Date()) {
    const intervalstr = interval.toLocaleLowerCase();
    const params: any = {};
    if (intervalstr === 'dia' || intervalstr === 'day') params.days = rangetime;
    if (intervalstr === 'mes' || intervalstr === 'month') params.months = rangetime;
    if (intervalstr === 'año' || intervalstr === 'year') params.years = rangetime;
    if (intervalstr === 'semana' || intervalstr === 'week') params.weeks = rangetime;

    const newdate = add(date, params);
    return this.datetimeCL(format, newdate);
  }

  /**
   * Convierte una fecha a formato ISO string con zona horaria local.
   * @param date - La fecha a convertir
   * @returns String en formato ISO con el offset de zona horaria (ej: "2023-12-25T10:30:00.000+05:30")
   */
  private static toISOLocal(date: Date) {
    const timezone = (n) => ('0' + n).slice(-2);
    let off = date.getTimezoneOffset();
    const sign = off < 0 ? '+' : '-';
    off = Math.abs(off);

    return (
      new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, -1) +
      sign +
      timezone(off / 60 || 0) +
      ':' +
      timezone(off % 60)
    );
  }

  /**
   * Convierte una fecha a formato ISO local con la hora al inicio o final del día.
   * @param date - La fecha a convertir
   * @param period - Define si se ajusta al inicio ('start') o final ('end') del día. Por defecto 'start'
   * @returns La fecha en formato ISO local con la hora ajustada
   */
  private static toISOWithFullHourStartOrEndDay(date: Date, period: 'start' | 'end' = 'start') {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDay();
    let newDate = new Date();

    if (period === 'end') newDate = new Date(year, month, day, 23, 59, 59, 999);
    if (period === 'start') newDate = new Date(year, month, day, 0, 0, 0, 0);

    return this.toISOLocal(newDate);
  }

  /**
   * Convierte una fecha en formato 'YYYY-MM-DD' a formato 'DD-MM-YYYY'.
   *
   * @param dateString - Cadena de texto que representa la fecha en formato 'YYYY-MM-DD'.
   * @returns La fecha formateada en formato 'DD-MM-YYYY'. Si la cadena es vacía o nula, retorna una cadena vacía.
   */
  static formatDateToDDMMYYYY(dateString: string): string {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  }

  /**
   * Obtiene las fechas del primer y último día de un período especificado.
   *
   * @param period Cadena que representa el período en formato 'YYYYMM', donde 'YYYY' es el año y 'MM' el mes.
   * @returns Un objeto con las propiedades `firstDay` y `lastDay`, ambas en formato ISO string.
   */
  static getPeriodDates(period: string): { firstDay: string; lastDay: string } {
    const year = parseInt(period.substring(0, 4));
    const month = parseInt(period.substring(4, 6)) - 1; // 0-indexed

    const firstDay = new Date(year, month, 1).toISOString();
    const lastDay = new Date(year, month + 1, 0).toISOString();

    return { firstDay, lastDay };
  }

  /**
   * Genera períodos semanales entre dos fechas dadas.
   * 
   * @param startDate - Fecha de inicio del rango
   * @param endDate - Fecha de fin del rango
   * @returns Array de períodos con propiedades `dateFrom` y `dateTo` en formato 'yyyy-MM-dd'
   */
  static getWeeklyPeriods(startDate: Date, endDate: Date): { dateFrom: string, dateTo: string }[] {
    const periods: { dateFrom: string, dateTo: string }[] = [];

    // Parsear las fechas como strings para trabajar solo con YYYY-MM-DD
    const startStr = format(startDate, 'yyyy-MM-dd');
    const endStr = format(endDate, 'yyyy-MM-dd');

    // Recrear las fechas a partir de los strings para evitar problemas de zona horaria
    const [startYear, startMonth, startDay] = startStr.split('-').map(Number);
    const [endYear, endMonth, endDay] = endStr.split('-').map(Number);

    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);

    let currentMonday = startOfWeek(start, { weekStartsOn: 1 });

    let weekNum = 0;
    while (weekNum < 10 && currentMonday <= end) {
      weekNum++;

      const currentSunday = endOfWeek(currentMonday, { weekStartsOn: 1 });
      const periodStart = currentMonday < start ? start : currentMonday;

      let periodEnd = currentSunday;
      if (currentSunday > end) {
        // Calcular el día siguiente al último día del periodo
        let nextDay = endDay + 1;
        let nextMonth = endMonth;
        let nextYear = endYear;

        // Obtener el último día del mes actual
        const lastDayOfMonth = new Date(endYear, endMonth, 0).getDate();

        if (nextDay > lastDayOfMonth) {
          nextDay = 1;
          nextMonth = endMonth + 1;
          if (nextMonth > 12) {
            nextMonth = 1;
            nextYear = endYear + 1;
          }
        }

        periodEnd = new Date(nextYear, nextMonth - 1, nextDay);
      }

      periods.push({
        dateFrom: format(periodStart, 'yyyy-MM-dd'),
        dateTo: format(periodEnd, 'yyyy-MM-dd'),
      });
      currentMonday = addDays(currentSunday, 1);
    }
    return periods;
  }
}
